import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for creating a notification
export const CREATE_NOTIFICATION_MUTATION = gql`
  mutation CreateNotification($input: CreateNotificationInput!) {
    createNotification(input: $input) {
      id
      title
      content
      type
      scopeType
      scopeData
      isActive
      channel
      validityStart
      validityEnd
      createdAt
      createdBy
      createdByUser {
        id
        username
        name
      }
    }
  }
`;

// GraphQL query for fetching notifications
export const GET_NOTIFICATIONS_QUERY = gql`
 query GetAllNotifications {
  getAllNotifications {
    id
    title
    content
    type
    scopeType
    scopeData
    isActive
    validityStart
    validityEnd
    createdAt
    updatedAt
    channel
    createdBy
    createdByUser {
      id
      username
      name
    }
    recipients {
      id
      userId
      status
      readAt
      createdAt
      user {
        id
        username
      }
    }
  }
}
`;

// GraphQL mutation for updating a notification
export const UPDATE_NOTIFICATION_MUTATION = gql`
  mutation UpdateNotification($input: UpdateNotificationInput!) {
  updateNotification(input: $input) {
    id
    title
    content
    type
    scopeType
    scopeData
    isActive
    validityStart
    validityEnd
    createdAt
    updatedAt
    createdBy
    channel
    createdByUser {
      id
      username
      name
    }
  }
}
`;


// GraphQL mutation for canceling a notification
export const CANCEL_NOTIFICATION_MUTATION = gql`
 mutation CancelNotification($id: Int!) {
  cancelNotification(id: $id)
}
`;

// Types for notification operations
export interface CreateNotificationInput {
  title: string;
  content: string;
  type?: string;
  scopeType: 'all_users' | 'hierarchy' | 'by_role' | 'selected_users';
  scopeData?: any | null;
  isActive?: boolean;
  validityStart?: string | null;
  validityEnd?: string | null;
  channel?: 'popup' | 'push' | 'instant'
}

export interface UpdateNotificationInput {
  id: number;
  title?: string;
  content?: string;
  isActive?: boolean;
  validityStart?: string | null;
  validityEnd?: string | null;
}

export interface NotificationFiltersInput {
  isActive?: boolean;
  scopeType?: string;
  type?: string;
  validFrom?: string;
  validTo?: string;
}

export interface NotificationUser {
  id: number;
  username: string;
  name: string | null;
}

export interface NotificationResponse {
  id: number;
  title: string;
  content: string;
  type: string | null;
  scopeType: string;
  scopeData: any | null;
  isActive: boolean;
  validityStart: string | null;
  validityEnd: string | null;
  createdAt: string;
  createdBy: number;
  createdByUser: NotificationUser | null;
  channel:  'popup' | 'push' | 'instant';
}

export interface GetNotificationsResponse {
  notifications: NotificationResponse[];
}

// Notification service class
export class NotificationService {
  private static instance: NotificationService;

  private constructor() { }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Create a new notification
   */
  async createNotification(
    input: CreateNotificationInput,
  ): Promise<NotificationResponse> {
    try {
      const response = await apolloClient.mutate({
        mutation: CREATE_NOTIFICATION_MUTATION,
        variables: { input },
      });

      if (response.errors && response.errors.length > 0) {
        throw response.errors[0];
      }

      if (!response.data || !response.data.createNotification) {
        throw new Error('No data returned from create notification mutation');
      }

      return response.data.createNotification as NotificationResponse;
    } catch (error: any) {
      console.error('Error creating notification:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  /**
   * Fetch notifications with optional filters
   */
  async getNotifications(
    filters?: NotificationFiltersInput,
  ): Promise<NotificationResponse[]> {
    try {
      const response = await apolloClient.query({
        query: GET_NOTIFICATIONS_QUERY,
        variables: { filters },
        fetchPolicy: 'network-only',
      });

      if (response.errors && response.errors.length > 0) {
        throw response.errors[0];
      }

      if (!response.data || !response.data.getAllNotifications) {
        throw new Error('No data returned from get notifications query');
      }

      return response.data.getAllNotifications as NotificationResponse[];
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  /**
   * Update an existing notification
   */
  async updateNotification(
    id: number,
    input: Omit<UpdateNotificationInput, 'id'>,
  ): Promise<NotificationResponse> {
    try {
      const updateInput = { id, ...input };

      const response = await apolloClient.mutate({
        mutation: UPDATE_NOTIFICATION_MUTATION,
        variables: { input: updateInput },
      });

      if (response.errors && response.errors.length > 0) {
        throw response.errors[0];
      }

      if (!response.data || !response.data.updateNotification) {
        throw new Error('No data returned from update notification mutation');
      }

      return response.data.updateNotification as NotificationResponse;
    } catch (error: any) {
      console.error('Error updating notification:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  /**
   * Cancel/Delete a notification
   */
  async deleteNotification(id: number): Promise<boolean> {
    try {
      const response = await apolloClient.mutate({
        mutation: CANCEL_NOTIFICATION_MUTATION,
        variables: { id },
      });

      if (response.errors && response.errors.length > 0) {
        throw response.errors[0];
      }

      return response.data?.cancelNotification || false;
    } catch (error: any) {
      console.error('Error canceling notification:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();

// Export convenience functions
export async function createNotification(
  input: CreateNotificationInput,
): Promise<NotificationResponse> {
  return notificationService.createNotification(input);
}

export async function getNotifications(
  filters?: NotificationFiltersInput,
): Promise<NotificationResponse[]> {
  return notificationService.getNotifications(filters);
}

export async function updateNotification(
  id: number,
  input: UpdateNotificationInput,
): Promise<NotificationResponse> {
  return notificationService.updateNotification(id, input);
}

export async function deleteNotification(id: number): Promise<boolean> {
  return notificationService.deleteNotification(id);
}
